<template>
    <div v-if="init" class="es-form loader text-center py-5">
        <div class="spinner-border text-success" role="status">
            <span class="sr-only">Loading...</span>
        </div>
    </div>
    <div v-else-if="final" class="es-form p-5">
        <div class="alert alert-primary" role="alert">
            {{ final_alert }}
        </div>
    </div>
    <div v-else id="es-form" class="es-form p-3">

        <div class="row mb-3">
            <div class="col input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <i class="fas fa-user"></i>
                    </span>
                </div>
                <input type="text"
                       v-model="formData.es_name"
                       id="es_name"
                       name="es_name"
                       class="form-control"
                       :disabled="loading"
                       :class="bindFieldClasses('es_name')"
                       :placeholder="field_labels.es_name"
                >
                <invalid-feedback :errors="bindFieldErrors('es_name')"></invalid-feedback>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col-md-6 input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <i class="fas fa-mobile-alt"></i>
                    </span>
                </div>
                <input type="text"
                       v-model="formData.es_phone"
                       id="es_phone"
                       name="es_phone"
                       v-mask="'+38 (099) 999 99 99'"
                       :disabled="loading"
                       class="form-control"
                       :class="bindFieldClasses('es_phone')"
                       :placeholder="field_labels.es_phone"
                >
                <invalid-feedback :errors="bindFieldErrors('es_phone')"></invalid-feedback>
            </div>
            <div class="col-md-6 mt-3 mt-md-0 input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <i class="fas fa-envelope"></i>
                    </span>
                </div>
                <input type="text"
                       v-model="formData.es_email"
                       id="es_email"
                       name="es_email"
                       :disabled="loading"
                       class="form-control"
                       :class="bindFieldClasses('es_email')"
                       :placeholder="field_labels.es_email"
                >
                <invalid-feedback :errors="bindFieldErrors('es_email')"></invalid-feedback>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <i class="fas fa-tag"></i>
                    </span>
                </div>
                <input type="text"
                       v-model="formData.es_object"
                       id="es_object"
                       name="es_object"
                       :disabled="loading"
                       class="form-control"
                       :class="bindFieldClasses('es_object')"
                       :placeholder="field_labels.es_object"
                >
                <invalid-feedback :errors="bindFieldErrors('es_object')"></invalid-feedback>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <i class="fas fa-map-marker-alt"></i>
                    </span>
                </div>
                <input type="text"
                       v-model="formData.es_address"
                       id="es_address"
                       name="es_address"
                       :disabled="loading"
                       class="form-control"
                       :class="bindFieldClasses('es_address')"
                       :placeholder="field_labels.es_address"
                >
                <invalid-feedback :errors="bindFieldErrors('es_address')"></invalid-feedback>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col-lg-5 input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <i class="far fa-calendar-check"></i>
                    </span>
                </div>
                <datepicker
                    v-model="datePickerValue"
                    name="es_period"
                    :disabled="loading"
                    :placeholder="field_labels.es_period"
                    :language="datepickerLang"
                    class="form-control"
                    :class="bindFieldClasses('es_period')"
                ></datepicker>
                <invalid-feedback :errors="bindFieldErrors('es_period')"></invalid-feedback>
            </div>
            <div class="col-lg-7 mt-3 mt-lg-0 input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <i class="fas fa-history"></i>
                    </span>
                </div>
                <div
                    class="form-control form-control--es_tender_status d-flex flex-column flex-lg-row align-items-start align-items-lg-center"
                    :class="bindFieldClasses('es_tender_status')"
                >
                    <label class="mr-2 mb-0">{{ field_labels.es_tender_status }}:</label>
                    <div v-for="option in tender_status_options"
                         :key="option"
                         class="form-check d-flex align-items-center mr-3"
                    >
                        <input
                            class="form-check-input"
                            type="radio"
                            :disabled="loading"
                            v-model="formData.es_tender_status"
                            name="es_tender_status"
                            :id="'es_tender_status_' + option"
                            :value="option"
                        >
                        <label class="form-check-label" :for="'es_tender_status_' + option">{{ option }}</label>
                    </div>
                </div>
                <invalid-feedback :errors="bindFieldErrors('es_tender_status')"></invalid-feedback>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col-lg-4">
                <div class="custom-file">
                    <input type="file"
                           v-on:change="handleFileUpload('es_contacts')"
                           ref="es_contacts"
                           name="es_contacts"
                           :disabled="loading"
                           class="custom-file-input"
                           :class="bindFieldClasses('es_contacts')"
                           id="es_contacts"
                    >
                    <label class="custom-file-label" for="es_contacts">{{ bindFileLabel('es_contacts') }}</label>
                    <invalid-feedback :errors="bindFieldErrors('es_contacts')"></invalid-feedback>
                </div>
            </div>
            <div class="col-lg-4 mt-3 mt-lg-0">
                <div class="custom-file">
                    <input type="file"
                           v-on:change="handleFileUpload('es_spec')"
                           ref="es_spec"
                           name="es_spec"
                           :disabled="loading"
                           class="custom-file-input"
                           :class="bindFieldClasses('es_spec')"
                           id="es_spec"
                    >
                    <label class="custom-file-label" for="es_spec">{{ bindFileLabel('es_spec') }}</label>
                    <invalid-feedback :errors="bindFieldErrors('es_spec')"></invalid-feedback>
                </div>
            </div>
            <div class="col-lg-4 mt-3 mt-lg-0">
                <div class="custom-file">
                    <input type="file"
                           v-on:change="handleFileUpload('es_file')"
                           ref="es_file"
                           name="es_file"
                           :disabled="loading"
                           class="custom-file-input"
                           :class="bindFieldClasses('es_file')"
                           id="es_file"
                    >
                    <label class="custom-file-label" for="es_file">{{ bindFileLabel('es_file') }}</label>
                    <invalid-feedback :errors="bindFieldErrors('es_file')"></invalid-feedback>
                </div>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col">
                    <textarea class="form-control"
                              v-model="formData.es_comment"
                              id="es_comment"
                              name="es_comment"
                              :disabled="loading"
                              :class="bindFieldClasses('es_comment')"
                              :placeholder="field_labels.es_comment"
                    >
                    </textarea>
                <invalid-feedback :errors="bindFieldErrors('es_comment')"></invalid-feedback>
            </div>
        </div>
        <div v-if="GOOGLE_RECAPTCHA_KEY" 
             class="form-group form-group--recaptcha"
             :class="bindFieldClasses('g-recaptcha-response')"
        >
            <vue-recaptcha
                :sitekey="GOOGLE_RECAPTCHA_KEY"
                :loadRecaptchaScript="true"
                @verify="isVerifyRecaptha = true"
                @expired="isVerifyRecaptha = false"
                @error="isVerifyRecaptha = false"
            >

            </vue-recaptcha>
            <invalid-feedback :errors="bindFieldErrors('g-recaptcha-response')"></invalid-feedback>
        </div>
        <div class="row mb-3">
            <div class="col">

                <button id="es-form-submit" class="btn btn-success" :disabled="isSubmitDisabled" @click="submit">
                    <span v-if="loading" class="spinner-border spinner-border-sm" role="status"
                          aria-hidden="true"></span>
                    {{ submit_text }}
                </button>

            </div>

        </div>
        <div class="row">
            <div class="col">
                <p>{{ terms_alert }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import InvalidFeedback from './components/InvalidFeedback.vue'

// @see more https://www.npmjs.com/package/vuejs-datepicker
import Datepicker from 'vuejs-datepicker'
import {en, uk, ru} from 'vuejs-datepicker/dist/locale'

// @see https://www.npmjs.com/package/vue-recaptcha
import VueRecaptcha from 'vue-recaptcha';

export default {
	name: 'ProjectEstimation',
	components: {InvalidFeedback, Datepicker, VueRecaptcha},
	data() {
		return {
			init: true,
			final: false,
			loading: false,
			verify: 'project.estimation',
			datePickerValue: undefined,
			datepickerLanguages: {
				en: en,
				ru: ru,
				uk: uk
			},
			isVerifyRecaptha: false,
			final_alert: null,
			terms_alert: null,
			submit_text: null,
			language: 'en',
			GOOGLE_RECAPTCHA_KEY: null,
			fileNames: {
				es_contacts: null,
				es_spec: null,
				es_file: null
			},
			field_labels: {
				es_name: null,
				es_phone: null,
				es_email: null,
				es_object: null,
				es_address: null,
				es_tender_status: null,
				es_period: null,
				es_contacts: null,
				es_spec: null,
				es_file: null
			},
			tender_status_options: [],
			formData: {
				es_name: null,
				es_phone: null,
				es_email: null,
				es_object: null,
				es_address: null,
				es_tender_status: null,
				es_period: null,
				es_contacts: null,
				es_spec: null,
				es_file: null
			},
			errors: {}
		}
	},
	computed: {
		datepickerLang() {
			if (!this.datepickerLanguages[this.language]) {
				return this.datepickerLanguages.en
			}

			return this.datepickerLanguages[this.language]
		},
		isSubmitDisabled() {
			const result = !this.GOOGLE_RECAPTCHA_KEY || this.loading || !this.isVerifyRecaptha

			return result
		}
	},
	watch: {
		datePickerValue(value) {
            const format = new Date(value)
			this.formData.es_period = format.toDateString()
		}
	},
	created() {
		this.fetchConfig()
	},
	methods: {
		fetchConfig() {
			this.init = true
			this.final = false

            const url = process.env.VUE_APP_API_URL
			axios.get(url)
					.then(this.initConfig)
					.catch(error => {
						console.log(error.response.data);
					});
		},
		initConfig(response) {
			this.language = response.data.language
			this.GOOGLE_RECAPTCHA_KEY = response.data.GOOGLE_RECAPTCHA_KEY
			this.field_labels = response.data.field_labels
			this.tender_status_options = response.data.tender_status_options
			this.final_alert = response.data.final_alert
			this.terms_alert = response.data.terms_alert
			this.submit_text = response.data.submit_text
			this.init = false
		},
		bindFileLabel(ref) {
			return this.fileNames[ref] ? this.fileNames[ref] : this.field_labels[ref]
		},
		bindFieldClasses(field) {
			return this.errors[field] ? 'is-invalid' : ''
		},
		bindFieldErrors(field) {
			return this.errors[field] ? this.errors[field] : []
		},
		resetErrors() {
			this.errors = Object.assign({})
		},
		customDateFormatter(date) {
			return 'ssss';
		},
		handleFileUpload(ref) {
			const file = this.$refs[ref].files[0]

			if (!file) {
				this.fileNames[ref] = null
				this.formData[ref] = null

				return
			}

			this.formData[ref] = file
			this.fileNames[ref] = file.name
			// console.log(file.name)
		},
		submit(e) {
			this.resetErrors()

			this.loading = true
			this.final = false

			let formData = new FormData()

			for (const field in this.formData) {
				let fileName = ''

				if (this.formData[field]) {
					if (this.fileNames[field]) {
						formData.append(field, this.formData[field], this.fileNames[field])
					} else {
						formData.append(field, this.formData[field])
					}
				}
			}

			const config = {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			}

			formData.append('_route', this.verify)

			const url = process.env.VUE_APP_API_URL
            
			axios.post(url, formData, config)
					.then(response => {
						console.log(response.data)
						this.loading = false
						this.final = true
					})
					.catch(error => {
						if (error.response.data.errors) {
							this.errors = Object.assign({}, error.response.data.errors)
							console.log(error.response.data);
						}

						this.loading = false
					});
		}
	}
}
</script>

<style lang="scss">
    $border-color: #ced4da;
    $regular: #636363;
    $title: #252525;

    .es-form {
        border: $border-color solid 1px;
        border-radius: 4px;
        font-family: 'Cera Pro Regular';

        label {
            font-family: Cera Pro Regular;
            font-size: 16px;
            color: $regular;

            &.form-check-label {
                color: $title;
            }
        }

        .input-group-prepend {
            .input-group-text {
                width: 41px;
            }

        }

        .input-group-text {
            height: 38px;
        }

        .input-group--file {
            .form-control {
                height: auto !important;
            }
        }

        label.custom-file-label {
            &:after {
                font-family: FontAwesome;
                content: '\f0c6'
            }
        }

        .form-check-input {
            margin-top: 0.1rem;
        }

        .vdp-datepicker {
            padding-left: 0;
            padding-right: 0;
            padding-top: 0;

            input {
                width: 100%;
                border: none;
                line-height: normal;
                padding: 9px 4px 7px 8px;
                margin: 0;
            }
        }

        .form-control--es_tender_status {
            height: auto;
        }
    }
</style>
